import {
  Room,
  RoomEvent,
  Track,
  VideoPresets,
  ConnectionState,
  createLocalTracks,
} from 'livekit-client';

// Development-only ICE diagnostic hook: capture RTCPeerConnection instances
// created by the browser so the diagnostic overlay can inspect selected
// candidate pairs without forking LiveKit. Only installed when the backend has
// injected the dev-relay-test flag on the <html> element.
if (typeof window !== 'undefined' && typeof document !== 'undefined' && document.documentElement.dataset.devRelayTest === 'true') {
  const OriginalRTCPeerConnection = window.RTCPeerConnection;
  if (OriginalRTCPeerConnection && !window.__tmPeerConnections) {
    window.__tmPeerConnections = [];
    window.RTCPeerConnection = function(...args) {
      const pc = new OriginalRTCPeerConnection(...args);
      window.__tmPeerConnections.push(pc);
      return pc;
    };
    Object.setPrototypeOf(window.RTCPeerConnection, OriginalRTCPeerConnection);
    window.RTCPeerConnection.prototype = OriginalRTCPeerConnection.prototype;
  }
}

/**
 * Thin wrapper around a LiveKit Room that renders a simple video grid:
 * the active speaker (therapist, when present) large, everyone else as
 * gallery thumbnails. Shared between the therapist and client front ends.
 */
export class SessionView {
  constructor({ gridEl, statusEl, identityPrefix }) {
    this.gridEl = gridEl;
    this.statusEl = statusEl;
    this.identityPrefix = identityPrefix || 'therapist:';
    this.room = null;
    this.connectionState = 'idle'; // idle | connecting | connected | reconnecting | disconnected | ended
    this._onConnectionStateChange = null;
    this._onDisconnected = null;
    this._deviceChangeHandler = null;
    this._onDevicesChanged = null;
    this.ended = false;
  }

  setStatus(text) {
    if (this.statusEl) this.statusEl.textContent = text;
  }

  onConnectionStateChange(fn) { this._onConnectionStateChange = fn; }
  onDisconnected(fn) { this._onDisconnected = fn; }
  onDevicesChanged(fn) { this._onDevicesChanged = fn; }

  _setConnectionState(state) {
    this.connectionState = state;
    if (this._onConnectionStateChange) this._onConnectionStateChange(state);
  }

  _devRelayMode() {
    if (typeof window === 'undefined' || typeof document === 'undefined') return false;
    // Only allow the relay test mode in development. The dev flag is injected
    // by the backend as a data attribute on the <html> element; the query
    // parameter is what actually activates it per-page-load.
    if (document.documentElement.dataset.devRelayTest !== 'true') return false;
    const params = new URLSearchParams(window.location.search);
    return params.get('forceRelay') === '1';
  }

  _devDiagnosticsEnabled() {
    if (typeof window === 'undefined' || typeof document === 'undefined') return false;
    return document.documentElement.dataset.devRelayTest === 'true';
  }

  _showDiagnostics() {
    if (typeof document === 'undefined') return;
    const existing = document.getElementById('tm-dev-diagnostics');
    if (existing) return;
    const panel = document.createElement('div');
    panel.id = 'tm-dev-diagnostics';
    panel.style.cssText = 'position:fixed;top:12px;right:12px;z-index:9999;max-width:360px;background:rgba(0,0,0,.85);color:#0f0;font-family:monospace;font-size:12px;padding:10px;border-radius:6px;white-space:pre-wrap;word-break:break-all;pointer-events:none;line-height:1.4;';
    document.body.appendChild(panel);
  }

  _diagText() {
    const el = document.getElementById('tm-dev-diagnostics');
    return el ? el.textContent : '';
  }

  _diag(lines) {
    const el = document.getElementById('tm-dev-diagnostics');
    if (!el) return;
    const text = Array.isArray(lines) ? lines.join('\n') : String(lines);
    el.textContent = text;
  }

  _findPeerConnections() {
    const candidates = [];
    if (this.room && this.room.engine && this.room.engine.pc) candidates.push(this.room.engine.pc);
    if (typeof window !== 'undefined' && window.__tmPeerConnections) {
      for (const pc of window.__tmPeerConnections) {
        if (!candidates.includes(pc)) candidates.push(pc);
      }
    }
    return candidates;
  }

  async _pollIceStats() {
    const pcs = this._findPeerConnections();
    if (pcs.length === 0) {
      this._diag([
        `Mode: ${this._devRelayMode() ? 'FORCED RELAY' : 'normal ICE'}`,
        'State: ' + (this.connectionState || 'unknown'),
        'Detailed ICE stats: use browser WebRTC internals',
      ]);
      return;
    }
    try {
      let best = null;
      for (const pc of pcs) {
        const stats = await pc.getStats();
        const candidates = new Map();
        let selectedPairId = null;
        for (const report of stats.values()) {
          if (report.type === 'local-candidate' || report.type === 'remote-candidate') {
            candidates.set(report.id, report);
          }
          if (report.type === 'candidate-pair' && report.selected) {
            selectedPairId = report.localCandidateId;
          }
          if (report.type === 'transport' && report.selectedCandidatePairId) {
            const pair = stats.get(report.selectedCandidatePairId);
            if (pair) selectedPairId = pair.localCandidateId;
          }
        }
        if (selectedPairId) {
          const local = candidates.get(selectedPairId);
          const pairReport = Array.from(stats.values()).find((r) => r.type === 'candidate-pair' && r.selected);
          const remote = pairReport && candidates.get(pairReport.remoteCandidateId);
          best = { local, remote };
          break;
        }
      }
      const info = [
        `Mode: ${this._devRelayMode() ? 'FORCED RELAY' : 'normal ICE'}`,
        best && best.local ? `Local: ${best.local.candidateType || '?'} ${best.local.protocol || ''} ${best.local.ip || best.local.address || ''}:${best.local.port || ''}` : 'Local: unknown',
        best && best.remote ? `Remote: ${best.remote.candidateType || '?'} ${best.remote.protocol || ''}` : 'Remote: unknown',
        best ? 'Pair: selected' : 'Pair: none selected',
      ];
      this._diag(info);
    } catch (e) {
      this._diag([`Mode: ${this._devRelayMode() ? 'FORCED RELAY' : 'normal ICE'}`, `Stats error: ${e.message}`]);
    }
  }

  async connect({ url, token, turnCredentials, preferredCameraId, preferredMicId } = {}) {
    const rtcConfig = {};
    const forceRelay = this._devRelayMode();
    if (forceRelay) {
      rtcConfig.iceTransportPolicy = 'relay';
    }
    if (turnCredentials) {
      rtcConfig.iceServers = [
        {
          urls: turnCredentials.urls,
          username: turnCredentials.username,
          credential: turnCredentials.credential,
        },
      ];
    }

    this._setConnectionState('connecting');
    this.setStatus(forceRelay ? 'Connecting (forced TURN relay)...' : 'Connecting...');

    this.room = new Room({
      adaptiveStream: true,
      dynacast: true,
      videoCaptureDefaults: {
        resolution: VideoPresets.h1080.resolution,
      },
      publishDefaults: {
        simulcast: true,
        videoSimulcastLayers: [VideoPresets.h180, VideoPresets.h360, VideoPresets.h720],
      },
    });

    this.registerRoomEvents();
    this._startDeviceHotplug();

    if (this._devDiagnosticsEnabled()) {
      this._showDiagnostics();
      this._diag([`Mode: ${forceRelay ? 'FORCED RELAY' : 'normal ICE'}`, 'Connecting...']);
    }

    await this.room.connect(url, token, { rtcConfig });
    await this._enableCameraAndMicrophone({ preferredCameraId, preferredMicId });
    this._setConnectionState('connected');
    this.setStatus(forceRelay ? 'Connected (forced TURN relay)' : 'Connected');
    this.renderAll();

    if (this._devDiagnosticsEnabled()) {
      this._showDiagnostics();
      this._diag([`Mode: ${forceRelay ? 'FORCED RELAY' : 'normal ICE'}`, 'Connected']);
      this._pollIceStats();
      this._iceStatsInterval = setInterval(() => this._pollIceStats(), 3000);
    }

    return this.room;
  }

  async _enableCameraAndMicrophone({ preferredCameraId, preferredMicId } = {}) {
    const lp = this.room.localParticipant;
    const cameraOptions = preferredCameraId ? { deviceId: preferredCameraId } : undefined;
    const micOptions = preferredMicId ? { deviceId: preferredMicId } : undefined;

    try {
      await lp.setCameraEnabled(true, cameraOptions);
    } catch (err) {
      console.warn('Failed to enable camera with preferred device, falling back', err);
      await lp.setCameraEnabled(true);
    }

    try {
      await lp.setMicrophoneEnabled(true, micOptions);
    } catch (err) {
      console.warn('Failed to enable microphone with preferred device, falling back', err);
      await lp.setMicrophoneEnabled(true);
    }
  }

  registerRoomEvents() {
    const room = this.room;
    const rerender = () => this.renderAll();

    room.on(RoomEvent.ParticipantConnected, rerender);
    room.on(RoomEvent.ParticipantDisconnected, rerender);
    room.on(RoomEvent.TrackSubscribed, rerender);
    room.on(RoomEvent.TrackUnsubscribed, rerender);
    room.on(RoomEvent.LocalTrackPublished, rerender);
    room.on(RoomEvent.LocalTrackUnpublished, rerender);
    room.on(RoomEvent.ActiveSpeakersChanged, rerender);
    room.on(RoomEvent.ConnectionQualityChanged, rerender);
    room.on(RoomEvent.ConnectionStateChanged, (state) => {
      if (state === ConnectionState.Reconnecting) {
        this._setConnectionState('reconnecting');
        this.setStatus('Connection interrupted \u2013 reconnecting\u2026');
      } else if (state === ConnectionState.Connected) {
        this._setConnectionState('connected');
        this.setStatus('Connected');
      } else if (state === ConnectionState.Disconnected) {
        if (!this.ended) {
          this._setConnectionState('disconnected');
          this.setStatus('Connection lost');
        }
      }
    });
    room.on(RoomEvent.Disconnected, () => {
      if (!this.ended) {
        this._setConnectionState('disconnected');
        this.setStatus('Connection lost');
      }
      if (this._onDisconnected) this._onDisconnected();
    });
  }

  _startDeviceHotplug() {
    if (!navigator.mediaDevices || !navigator.mediaDevices.addEventListener) return;
    this._deviceChangeHandler = () => {
      if (this._onDevicesChanged) this._onDevicesChanged();
    };
    navigator.mediaDevices.addEventListener('devicechange', this._deviceChangeHandler);
  }

  _stopDeviceHotplug() {
    if (this._deviceChangeHandler && navigator.mediaDevices) {
      navigator.mediaDevices.removeEventListener('devicechange', this._deviceChangeHandler);
      this._deviceChangeHandler = null;
    }
  }

  markEnded() {
    this.ended = true;
    this._setConnectionState('ended');
    this.setStatus('Session ended');
  }

  allParticipants() {
    if (!this.room) return [];
    return [this.room.localParticipant, ...this.room.remoteParticipants.values()];
  }

  findFeaturedParticipant() {
    const participants = this.allParticipants();
    const therapist = participants.find((p) => p.identity.startsWith(this.identityPrefix));
    return therapist || participants[0];
  }

  renderAll() {
    if (!this.gridEl) return;
    this.gridEl.innerHTML = '';
    const participants = this.allParticipants();
    const featured = this.findFeaturedParticipant();

    participants.forEach((participant) => {
      const tile = this.buildTile(participant, participant === featured);
      this.gridEl.appendChild(tile);
    });
  }

  buildTile(participant, isFeatured) {
    const tile = document.createElement('div');
    tile.className = isFeatured ? 'tile tile-featured' : 'tile tile-thumb';

    const videoEl = document.createElement('video');
    videoEl.autoplay = true;
    videoEl.playsInline = true;
    videoEl.muted = participant.isLocal;

    const videoPub = [...participant.trackPublications.values()].find(
      (p) => p.kind === Track.Kind.Video && p.track
    );
    if (videoPub && videoPub.track) {
      videoPub.track.attach(videoEl);
    }

    const audioPub = [...participant.trackPublications.values()].find(
      (p) => p.kind === Track.Kind.Audio && p.track
    );
    const audioEl = document.createElement('audio');
    audioEl.autoplay = true;
    if (!participant.isLocal && audioPub && audioPub.track) {
      audioPub.track.attach(audioEl);
    }

    const label = document.createElement('div');
    label.className = 'tile-label';
    const roleTag = this.getRoleTag(participant);
    label.textContent = `${participant.name || participant.identity}${participant.isLocal ? ' (you)' : ''}${roleTag ? ' · ' + roleTag : ''}`;

    const quality = document.createElement('span');
    quality.className = `quality quality-${participant.connectionQuality}`;
    label.appendChild(quality);

    tile.appendChild(videoEl);
    tile.appendChild(audioEl);
    tile.appendChild(label);
    return tile;
  }

  getRoleTag(participant) {
    const identity = participant.identity || '';
    if (identity.startsWith('supervisor:')) return 'Observer';
    if (identity.startsWith('provider:')) return 'Provider';
    return '';
  }

  async toggleMic() {
    const enabled = this.room.localParticipant.isMicrophoneEnabled;
    await this.room.localParticipant.setMicrophoneEnabled(!enabled);
    return !enabled;
  }

  async toggleCamera() {
    const enabled = this.room.localParticipant.isCameraEnabled;
    await this.room.localParticipant.setCameraEnabled(!enabled);
    return !enabled;
  }

  async setCameraDevice(deviceId) {
    await this.room.switchActiveDevice('videoinput', deviceId);
  }

  async setMicDevice(deviceId) {
    await this.room.switchActiveDevice('audioinput', deviceId);
  }

  async setSpeakerDevice(deviceId) {
    try {
      await this.room.switchActiveDevice('audiooutput', deviceId);
    } catch (_e) {
      // Browser may not support audio output selection
    }
  }

  static speakerSelectionSupported() {
    // setSinkId is required for audio output device switching
    return typeof HTMLMediaElement !== 'undefined' && 'setSinkId' in HTMLMediaElement.prototype;
  }

  disconnect() {
    this._stopDeviceHotplug();
    if (this._iceStatsInterval) {
      clearInterval(this._iceStatsInterval);
      this._iceStatsInterval = null;
    }
    if (this.room) this.room.disconnect();
  }
}

export { createLocalTracks };
